import { useTheme } from '@emotion/react';
import { CheckOne, Diamond, Lock, Mark, Time } from '@icon-park/react';
import { memo, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { LESSON_UNLOCK_DEBIT_AMOUNT } from '@didaskill/domain/wallet/constants';
import { Button, Tag, Typography } from '@didaskill/ui/design-system';
import { lessonsMessages, sequencesMessages } from '@didaskill/ui/intl';

import { LESSON_CARD_VARIANTS } from '../constants';
import messages from '../LessonCard.messages';
import * as styles from '../LessonCard.styles';

export type LessonCardFooterProps = {
  lessonCardVariant: LESSON_CARD_VARIANTS;
  isLessonExternal: boolean;
  progress?: number;
  sequencesCount?: number;
  nbRemainingDays?: number;
};

export const LessonCardFooterBase = ({
  lessonCardVariant,
  isLessonExternal,
  progress,
  sequencesCount,
  nbRemainingDays,
}: LessonCardFooterProps) => {
  const theme = useTheme();

  const { text, icon } = useMemo(() => {
    switch (lessonCardVariant) {
      case LESSON_CARD_VARIANTS.COMPLETED:
        return {
          text: (
            <Typography css={styles.completedText} variant={Typography.VARIANTS.BODY_SMALL_BOLD}>
              <FormattedMessage {...messages.COMPLETED} />
            </Typography>
          ),
          icon: <CheckOne size={16} fill={[theme.colors.success[500], theme.colors.success[25]]} />,
        };

      case LESSON_CARD_VARIANTS.DISABLED:
        return {
          text: (
            <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR}>
              <FormattedMessage
                {...lessonsMessages.AVAILABLE_IN}
                values={{
                  count: nbRemainingDays,
                }}
              />
            </Typography>
          ),
          icon: <Lock size={16} fill={[theme.colors.gray[500], theme.colors.gray[25]]} />,
        };

      case LESSON_CARD_VARIANTS.TO_START:
        return {
          text: (
            <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR} element="span">
              <FormattedMessage
                {...(isLessonExternal
                  ? sequencesMessages.MODULES_COUNT
                  : sequencesMessages.SEQUENCES_COUNT)}
                values={{ count: sequencesCount }}
              />
            </Typography>
          ),
          icon: <Mark size={16} fill={[theme.colors.primary[500], theme.colors.primary[25]]} />,
        };

      case LESSON_CARD_VARIANTS.IN_PROGRESS:
        return {
          text: (
            <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR}>
              <FormattedMessage
                {...messages.PROGRESS}
                values={{
                  progress,
                }}
              />
            </Typography>
          ),
          icon: <Time size={16} fill={[theme.colors.gray[200], theme.colors.gray[0]]} />,
        };

      case LESSON_CARD_VARIANTS.LOCKED:
        return {
          text: (
            <Typography
              className="hide-on-hover"
              variant={Typography.VARIANTS.BODY_SMALL_REGULAR}
              element="span"
            >
              <FormattedMessage
                {...(isLessonExternal
                  ? sequencesMessages.MODULES_COUNT
                  : sequencesMessages.SEQUENCES_COUNT)}
                values={{ count: sequencesCount }}
              />
            </Typography>
          ),
          icon: (
            <Tag
              className="hide-on-hover"
              variant={Tag.VARIANTS.PRIMARY}
              icon={<Diamond />}
              label={LESSON_UNLOCK_DEBIT_AMOUNT}
            />
          ),
        };

      default:
        return {
          text: null,
          icon: null,
        };
    }
  }, [isLessonExternal, lessonCardVariant, nbRemainingDays, progress, sequencesCount, theme]);

  return (
    <div css={styles.footer}>
      <div css={styles.info}>
        {text}

        {icon}
      </div>
      {lessonCardVariant === LESSON_CARD_VARIANTS.LOCKED && (
        <Button
          css={styles.lockedButton}
          className="display-on-hover"
          variant={Button.VARIANTS.PRIMARY}
          size={Button.SIZES.SMALL}
          iconPosition={Button.ICON_POSITIONS.RIGHT}
          icon={<Diamond />}
        >
          <FormattedMessage
            {...messages.LOCKED_BUTTON}
            values={{
              count: LESSON_UNLOCK_DEBIT_AMOUNT,
            }}
          />
        </Button>
      )}
    </div>
  );
};

LessonCardFooterBase.displayName = 'LessonCardFooter';

export const LessonCardFooter = memo(LessonCardFooterBase);
